import React, { useState } from "react";
import { apiPostBlob } from "@/lib/api";
import { Download, CheckCircle2 } from "lucide-react";
import ConsentMicrocopy from "@/components/ConsentMicrocopy";

/**
 * Email-gated PDF download card.
 * Props:
 * - endpoint: e.g. "/pdf/guide/doctors-high-earners" or "/pdf/calculator/life-insurance-needs"
 * - filename: download filename
 * - title, subtitle
 * - extraBody: object posted with email+name (e.g. {inputs, results} for calc reports)
 * - testIdPrefix
 */
export default function PdfDownloadCard({
  endpoint,
  filename,
  title = "Get the free PDF guide",
  subtitle = "Email it to me and start the download now.",
  extraBody = {},
  testIdPrefix = "pdf-card",
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/.+@.+\..+/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const blobData = await apiPostBlob(
        endpoint,
        { email, full_name: name || undefined, ...extraBody }
      );
      // Stream PDF to user
      const blob = new Blob([blobData], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDone(true);
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testIdPrefix}-success`} className="bg-white rounded-2xl border border-lw-gold/40 p-6 sm:p-7 flex items-start gap-3">
        <CheckCircle2 size={22} className="text-lw-gold flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-serif-display text-lg text-lw-navy">Downloaded.</p>
          <p className="text-sm text-lw-muted mt-1">Check your downloads folder — we&apos;ve also emailed you a backup link.</p>
        </div>
      </div>
    );
  }

  return (
    <form data-testid={`${testIdPrefix}-form`} onSubmit={submit} className="bg-white rounded-2xl border border-lw-gold/40 p-6 sm:p-7 space-y-3">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg, rgba(11,37,69,0.06), rgba(212,162,76,0.18))", color: "#0B2545" }}>
          <Download size={20} />
        </div>
        <div>
          <h3 className="font-serif-display text-xl text-lw-navy">{title}</h3>
          <p className="text-sm text-lw-muted mt-1">{subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          data-testid={`${testIdPrefix}-name`}
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
        />
        <input
          data-testid={`${testIdPrefix}-email`}
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-lg border border-lw-navy/15 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-lw-gold/40"
        />
      </div>
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        data-testid={`${testIdPrefix}-submit`}
        className="btn-gold w-full sm:w-auto justify-center disabled:opacity-60"
      >
        {busy ? "Generating PDF…" : "Download free PDF"} <Download size={16} />
      </button>
      <ConsentMicrocopy className="mt-1" />
    </form>
  );
}
